import { ApiError } from '@/shared/lib/api';
import type { RestaurantUsingMode, DisconnectBlockedBody } from './types';

/**
 * Thrown when DELETE /stripe-connect returns 400 with a list of
 * restaurants still configured for the mode being disconnected.
 * The UI surfaces this list with direct links to each restaurant's settings.
 */
export class DisconnectBlockedError extends Error {
  readonly status = 400;
  readonly blockingRestaurants: RestaurantUsingMode[];

  constructor(message: string, blockingRestaurants: RestaurantUsingMode[]) {
    super(message);
    this.name = 'DisconnectBlockedError';
    this.blockingRestaurants = blockingRestaurants;
  }
}

function readBlockingRestaurants(details: unknown): RestaurantUsingMode[] | null {
  if (!details || typeof details !== 'object') return null;
  const maybe = (details as DisconnectBlockedBody).blockingRestaurants;
  if (!Array.isArray(maybe)) return null;
  return maybe
    .filter(
      (r): r is RestaurantUsingMode =>
        !!r && typeof (r as RestaurantUsingMode).restaurantId === 'string' && typeof (r as RestaurantUsingMode).title === 'string',
    );
}

export function toDisconnectError(err: unknown): DisconnectBlockedError | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const list = readBlockingRestaurants(err.details);
  if (!list) return null;
  return new DisconnectBlockedError(err.message, list);
}
